import { useState } from 'react';
import { isEmpty, isNil, orderBy } from 'lodash';
import { Option } from '../../../types/common/table';
import { INITIAL_OPTION } from '../../../constants/service/common/tableConstants';

interface UseClientTableListProps<Item, Filter> {
  list: Item[];
  changeOption: (option: Option<Filter>) => void;
  option: Option<Filter>;
}

const useClientTableList = <Item extends Record<string, unknown>, Filter extends Record<string, unknown>>(
  props: UseClientTableListProps<Item, Filter>,
): { list: Item[]; totalCnt: number } => {
  const { list, option, changeOption } = props;
  const {
    sortOption = INITIAL_OPTION.sortOption,
    filterOption = INITIAL_OPTION.filterOption,
    pagination = INITIAL_OPTION.pagination,
  } = option;
  const [prevLength, setPrevLength] = useState(list.length);

  /**
   * 리스트가 삭제되어 현재 페이지와 싱크가 맞지 않을 경우 맞춰주는 로직
   * useEffect를 사용하지 않고 바로 setState로 싱크를 맞추는 이유는 불필요하게 렌더링을 두번하지 않기 위함
   * */
  if (list.length !== prevLength) {
    setPrevLength(list.length);

    const { pageNum, pageSize } = pagination;
    const lastPage = list.length === 0 ? 0 : Math.floor((list.length - 1) / pageSize);
    changeOption({ pagination: { pageSize, pageNum: lastPage < pageNum ? lastPage : pageNum } });
  }

  //필터할게 없으면 전체 리스트 리턴
  const filteredList = isEmpty(filterOption)
    ? list
    : list.filter((item) => {
        const filters = Object.entries(filterOption);

        return filters.every(([name, value]) => {
          if (isNil(value)) return true;

          if (Array.isArray(item[name]) && (item[name] as { id: number }[])[0]?.id) {
            const idList = (item[name] as { id: number }[]).map((item) => item.id);
            return idList.includes(value as number);
          }

          if (!isNaN(Number(value)) && !isNaN(Number(item[name]))) {
            return Number(value) === Number(item[name]);
          }

          if (typeof value === 'string' && typeof item[name] === 'string') {
            return (item[name] as string).includes(value);
          }

          return false;
        });
      });

  const sortedList = isEmpty(sortOption)
    ? filteredList
    : orderBy(
        filteredList,
        Object.keys(sortOption),
        Object.values(sortOption).map((isDesc) => (isDesc ? 'desc' : 'asc')),
      );

  const paginatedList = sortedList.slice(
    pagination.pageNum * pagination.pageSize,
    (pagination.pageNum + 1) * pagination.pageSize,
  );

  return {
    list: paginatedList,
    totalCnt: filteredList.length,
  };
};

export default useClientTableList;
